import React, { useState } from 'react';
import { Users, Plus, Upload, Edit, Trash2 } from 'lucide-react';
import type { User, CurrentUser, University } from '../../types';
import SearchInput from '../ui/SearchInput';
import StatusBadge from '../ui/StatusBadge';
import EmptyState from '../ui/EmptyState';
import ConfirmDialog from '../ui/ConfirmDialog';

interface UserTableProps {
  users: User[];
  currentUser: CurrentUser;
  universities: University[];
  onAdd: () => void;
  onEdit: (user: User) => void;
  onDelete: (userId: string) => void;
  onBulkUpload: () => void;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  currentUser,
  universities,
  onAdd,
  onEdit,
  onDelete,
  onBulkUpload,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = useState('');

  const filtered = users.filter(user => {
    const q = searchQuery.toLowerCase();
    return user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q);
  });

  const isSuperAdmin = currentUser.role === 'super_admin';
  const activeUsersCount = users.filter(u => u.status === 'active').length;

  const getUniversityName = (universityId: string | null) => {
    if (!universityId) return 'No university assigned';
    return universities.find(u => u.id === universityId)?.name || 'Unknown university';
  };

  const handleDeleteClick = (user: User) => {
    if (isSuperAdmin && user.role === 'admin') {
      const sameUniAdmins = users.filter(
        u => u.role === 'admin' && u.universityId === user.universityId && u.id !== user.id
      );
      if (sameUniAdmins.length === 0) {
        const uniName = getUniversityName(user.universityId);
        setConfirmDeleteMessage(
          `This is the last university admin for ${uniName}. Deleting them will leave the university without any administrators. Are you sure?`
        );
      } else {
        setConfirmDeleteMessage(`Are you sure you want to delete "${user.name}"?`);
      }
    } else {
      setConfirmDeleteMessage(`Are you sure you want to delete "${user.name}"?`);
    }
    setConfirmDelete(user);
  };

  const title = isSuperAdmin
    ? 'University Administrators'
    : `Users - ${getUniversityName(currentUser.universityId)}`;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              {title}
            </h2>
            <span className="ml-3 text-sm text-gray-600">
              ({users.length} {isSuperAdmin ? `Admin${users.length !== 1 ? 's' : ''}` : `User${users.length !== 1 ? 's' : ''}`})
            </span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onAdd}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {isSuperAdmin ? 'Admin' : 'User'}
            </button>
            <button
              onClick={onBulkUpload}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </button>
          </div>
        </div>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by name or email..."
        />
      </div>

      {!isSuperAdmin && (
        <div className="px-6 pt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-sm text-green-700">Activated Users</div>
              <div className="text-2xl font-bold text-green-800">{activeUsersCount}</div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        {filtered.length === 0 ? (
          <EmptyState
            icon={<Users className="w-8 h-8" />}
            title={searchQuery ? 'No users match your search' : 'No users yet'}
            description={searchQuery ? 'Try a different search term' : `Add your first ${isSuperAdmin ? 'administrator' : 'user'} to get started`}
          />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {isSuperAdmin ? 'Administrator' : 'User'}
                </th>
                {isSuperAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">University</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                        <Users className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  {isSuperAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getUniversityName(user.universityId)}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={user.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => onEdit(user)}
                      className="text-indigo-600 hover:text-indigo-900 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(user)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="Delete User"
          message={confirmDeleteMessage}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => {
            onDelete(confirmDelete.id);
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default UserTable;
